import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Col, Row, Tooltip } from 'antd';
import { BaseFormInputItem } from '@app/components/common/forms/components/BaseFormInputItem/BaseFormInputItem';
import { notificationController } from '@app/controllers/notificationController';
import { handleEncryptText } from '@app/utils/encryptText';

import ISendCommandService, { SendCommandService } from '@app/services/sendCommandService';
import { SendCommandEnum } from '@app/constants/enums/sendCommand';
import { CommandComponentHandle, CommandComponentProps } from '@app/constants/interfaces/command-interface';
import * as S from './styles';

const sendCommandService: ISendCommandService = new SendCommandService();

interface DirectMethodResponse {
  payload?: string;
  status?: number;
  mensagem?: string;
}

export const DirectMethodCommand = forwardRef<CommandComponentHandle, CommandComponentProps>(
  ({ deviceId, setReadyToSend, setLoading }, ref) => {
    const [methodName, setMethodName] = useState('');
    const [content, setContent] = useState('');
    const [response, setResponse] = useState<DirectMethodResponse | null>(null);

    const isContentValid = (raw: string): boolean => {
      const trimmed = raw.trim();
      if (!trimmed) return true;
      try {
        JSON.parse(trimmed);
        return true;
      } catch {
        return false;
      }
    };
    const formatPayload = (payload: string): string => {
      const raw = payload ?? '';
      const trimmedRaw = raw.trim();
      if (!trimmedRaw) return '';

      try {
        let parsed: any = JSON.parse(trimmedRaw);

        if (typeof parsed === 'string') {
          const inner = parsed.trim();
          const looksLikeJson =
            (inner.startsWith('{') && inner.endsWith('}')) || (inner.startsWith('[') && inner.endsWith(']'));

          if (looksLikeJson) {
            parsed = JSON.parse(inner);
          } else {
            return parsed;
          }
        }

        if (typeof parsed === 'object' && parsed !== null) {
          return JSON.stringify(parsed, null, 2);
        }

        return String(parsed);
      } catch {
        return raw;
      }
    };

    useImperativeHandle(ref, () => ({ sendCommand: handleSendCommand }));

    const handleSendCommand = async () => {
      if (!deviceId || !methodName.trim()) {
        notificationController.error({
          message: 'Erro!',
          description: 'Preencha o nome do método direto.',
        });
        return;
      }

      if (content && !isContentValid(content)) {
        notificationController.error({
          message: 'Formato inválido',
          description:
            'A carga deve ser um JSON válido (objeto, array) ou um valor único (ex.: número ou string entre aspas).',
        });
        return;
      }

      setLoading(true);
      setResponse(null);

      const message = content.trim() ? `${methodName.trim()}#${content.trim()}` : methodName.trim();

      try {
        const res = await sendCommandService.post('', {
          idDispositivo: deviceId,
          metodo: await handleEncryptText(SendCommandEnum.DIRECT_METHOD),
          mensagem: await handleEncryptText(message),
        });

        notificationController.success({ message: 'Comando enviado com sucesso.' });
        const responseData = res && typeof res === 'object' ? (res as DirectMethodResponse) : undefined;

        setResponse({
          payload: responseData?.payload ?? '',
          status: responseData?.status ?? 200,
          mensagem: responseData?.mensagem ?? 'OK',
        });
      } catch (err: unknown) {
        const error = err as {
          message?: string;
          response?: { data?: DirectMethodResponse; status?: number };
        };

        notificationController.error({
          message: 'Erro!',
          description: error?.message || 'Erro ao enviar comando de método direto.',
        });

        const data = error?.response?.data;
        setResponse({
          payload: data?.payload ?? '',
          status: data?.status ?? error?.response?.status ?? 0,
          mensagem: data?.mensagem ?? error?.message ?? 'Erro',
        });
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
      setReadyToSend(methodName.trim().length > 0);
    }, [methodName, setReadyToSend]);

    const prettyPayload = response?.payload ? formatPayload(response.payload) : '';

    return (
      <S.StyledBaseForm layout="vertical">
        <Row gutter={[0, 32]}>
          <Col span={24}>
            <BaseFormInputItem
              label={
                <span>
                  Nome do Método Direto
                  <Tooltip title="Nome do método a ser executado no dispositivo.">
                    <S.TooltipIcon />
                  </Tooltip>
                </span>
              }
              required
              style={{ width: '100%' }}
            >
              <S.StyledInput
                placeholder="Digite o nome do método direto"
                value={methodName}
                onChange={(e) => setMethodName(e.target.value)}
                title="Nome do método a ser executado no dispositivo."
              />
            </BaseFormInputItem>
          </Col>

          <Col span={24}>
            <BaseFormInputItem
              label={
                <span>
                  Conteúdo (Opcional)
                  <Tooltip title="Dados enviados ao método. Pode ser um valor único ou JSON.">
                    <S.TooltipIcon />
                  </Tooltip>
                </span>
              }
              style={{ width: '100%' }}
            >
              <S.StyledTextArea
                placeholder="Digite o conteúdo do método direto (opcional)"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={4}
                title="Dados enviados ao método. Pode ser um valor único ou JSON."
              />
            </BaseFormInputItem>
          </Col>

          {response && (
            <Col span={24}>
              <S.ResponseCard
                title="Resposta do Método Direto"
                size="small"
                $isSuccess={(response.status ?? 0) >= 200 && (response.status ?? 0) < 300}
              >
                <Row gutter={[8, 8]}>
                  <Col span={24}>
                    <strong>Status:</strong> {response.status}
                  </Col>
                  <Col span={24}>
                    <strong>Mensagem:</strong> {response.mensagem}
                  </Col>
                  {!!response.payload && (
                    <Col span={24}>
                      <strong>Payload:</strong>
                      <S.PayloadPre>{prettyPayload}</S.PayloadPre>
                    </Col>
                  )}
                </Row>
              </S.ResponseCard>
            </Col>
          )}
        </Row>
      </S.StyledBaseForm>
    );
  },
);

DirectMethodCommand.displayName = 'DirectMethodCommand';
